(function(){
// Custom elements inside an island must not connect until their payload is ready.
// Patch the lifecycle, not the constructor: framework constructors and native
// registration identity remain intact, including the dev hot-reload registry.
var islandPropsKey = Symbol.for("island.props");
var islandConnections = new WeakMap();
var islandAppliedProps = new WeakSet();
var islandMounts = new WeakMap();
var islandConnectKey = Symbol("island.connect");
var islandDisconnectKey = Symbol("island.disconnect");

function islandPrepareProps(el) {
  if (Object.prototype.hasOwnProperty.call(el, islandPropsKey)) return el[islandPropsKey];
  var script = el.querySelector(":scope > [data-props]");
  if (!script) return undefined;
  var value = new Function("return " + script.textContent)();
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError("Island props must be an object");
  }
  Object.defineProperty(el, islandPropsKey, { value: value, configurable: true });
  script.remove();
  return value;
}

function islandApplyProps(el, value) {
  if (!value || islandAppliedProps.has(el)) return;
  // component-register owns .props internally. Never hand it the caller's
  // object, which it can rewrite into property definitions during connection.
  el.props = { ...value };
  var deferredProps = el.constructor.def && el.constructor.def.props;
  Object.keys(value).forEach(function(key) {
    if (key === "props" || key === "__proto__" || key === "constructor" || key === "prototype" || (/^on/i.test(key) && key in HTMLElement.prototype)) return;
    var proto = el;
    var declared = false;
    while (proto && proto !== HTMLElement.prototype) {
      var descriptor = Object.getOwnPropertyDescriptor(proto, key);
      if (descriptor) {
        if (typeof descriptor.value === "function" || (descriptor.get && !descriptor.set)) return;
        declared = true;
        break;
      }
      proto = Object.getPrototypeOf(proto);
    }
    // Unknown names are safe expandos, but native DOM members must be explicitly
    // declared by the component before payload data may overwrite them.
    if (!declared && key in HTMLElement.prototype) {
      // Vue adopts declared own properties when connecting, before it creates
      // accessors. Do not route these values through coercing DOM setters.
      if (!deferredProps || !(Array.isArray(deferredProps) ? deferredProps.indexOf(key) !== -1 : Object.prototype.hasOwnProperty.call(deferredProps, key))) return;
      var nativeProto = HTMLElement.prototype;
      while (nativeProto) {
        var nativeProperty = Object.getOwnPropertyDescriptor(nativeProto, key);
        if (nativeProperty) {
          if (typeof nativeProperty.value === "function" || (nativeProperty.get && !nativeProperty.set)) return;
          break;
        }
        nativeProto = Object.getPrototypeOf(nativeProto);
      }
      Object.defineProperty(el, key, { value: value[key], writable: true, configurable: true, enumerable: true });
      return;
    }
    // An ES class-field default can shadow a framework's reactive accessor.
    // Remove only configurable data fields with an actual component setter.
    var own = Object.getOwnPropertyDescriptor(el, key);
    if (own && own.configurable && "value" in own) {
      var inherited = Object.getPrototypeOf(el);
      while (inherited && inherited !== HTMLElement.prototype) {
        var property = Object.getOwnPropertyDescriptor(inherited, key);
        if (property) {
          if (property.set) delete el[key];
          break;
        }
        inherited = Object.getPrototypeOf(inherited);
      }
    }
    el[key] = value[key];
  });
  islandAppliedProps.add(el);
}

(function() {
  var define = globalThis.customElements.define.bind(globalThis.customElements);
  var prepared = new WeakSet();
  globalThis.customElements.define = function(name, ctor, options) {
    if (name !== "island-element" && !prepared.has(ctor)) {
      prepared.add(ctor);
      Object.defineProperty(ctor.prototype, islandConnectKey, { value: ctor.prototype.connectedCallback, configurable: true });
      Object.defineProperty(ctor.prototype, islandDisconnectKey, { value: ctor.prototype.disconnectedCallback, configurable: true });
      Object.defineProperty(ctor.prototype, "connectedCallback", {
        configurable: true, writable: true,
        value: function() {
          var wrapper = this.closest && this.closest("island-element");
          if (wrapper) {
            islandConnections.set(this, false);
            return;
          }
          try {
            islandApplyProps(this, this[islandPropsKey]);
            islandConnections.set(this, true);
            var connect = ctor.prototype[islandConnectKey];
            var result = connect && connect.call(this);
            islandMounts.set(this, Promise.resolve(result));
            return result;
          } catch (error) {
            var failed = Promise.reject(error);
            failed.catch(function() {});
            islandMounts.set(this, failed);
            if (!this[islandPropsKey]) throw error;
          }
        }
      });
      Object.defineProperty(ctor.prototype, "disconnectedCallback", {
        configurable: true, writable: true,
        value: function() {
          if (islandConnections.get(this) === false) return;
          islandConnections.set(this, false);
          var disconnect = ctor.prototype[islandDisconnectKey];
          if (disconnect) return disconnect.call(this);
        }
      });
    }
    return define(name, ctor, options);
  };
})();

// Island runtime — hydrates <island-element> wrappers on demand.
// Embedded into the Go binary; shipped as-is to production themes and
// concatenated after island-dev.js for `dev`.

// Diagnostics are forwarded to the dev runtime when one is installed
// (island-dev.js); in production nothing listens and both are no-ops.
function islandRuntimeEvent(body) {
  try {
    var runtime = globalThis.__islandRuntimeDev;
    if (runtime && runtime.event) runtime.event(body);
  } catch(e) {}
}

function islandRuntimeError(title, err, context) {
  try {
    var runtime = globalThis.__islandRuntimeDev;
    if (runtime && runtime.error) {
      context = context || {};
      context.showBoundary = true;
      runtime.error("island-error", title, err, context);
      return;
    }
  } catch(e) {}
  console.error(err);
}

// Resilient import. Islands are bundled per component; framework packages and
// shared modules stay bare specifiers ("lit", "@shared/x") resolved by the
// theme's import map tag. Inside the theme customizer the editor
// injects its own scripts into <head> ahead of ours, and a browser applies
// only the FIRST import map it sees — so ours can be silently ignored and
// every island fails with "Failed to resolve module specifier". When a bare
// specifier fails, importIsland fetches the chunk, rewrites its bare imports
// from our own map (re-parsed from the DOM, which is still readable even when
// not applied) and imports the result as a Blob URL. Chunks have no relative
// imports, so one level of rewriting is complete. Production pages where the
// map applied never take this path.
var islandImportMap = null;
function islandReadImportMap() {
  if (islandImportMap) return islandImportMap;
  var map = {};
  var tags = typeof document !== "undefined" ? document.querySelectorAll('script[type="importmap"]') : [];
  for (var i = 0; i < tags.length; i++) {
    try {
      var parsed = JSON.parse(tags[i].textContent || "{}");
      var imports = parsed && parsed.imports;
      for (var key in imports) if (!(key in map)) map[key] = imports[key];
    } catch(e) {}
  }
  islandImportMap = map;
  return map;
}
// islandResolveSpecifier maps one bare specifier through the import map
// (exact key, then trailing-slash prefix keys); "" when unmapped.
function islandResolveSpecifier(spec, map) {
  var target = map[spec];
  if (!target) {
    for (var key in map) {
      if (key.slice(-1) === "/" && spec.indexOf(key) === 0) { target = map[key] + spec.slice(key.length); break; }
    }
  }
  if (!target) return "";
  try { return new URL(target, document.baseURI).href; } catch(e) { return target; }
}
var islandSpecifierPattern = /((?:from|import)\s*\(?\s*)(["'])([^"'./][^"']*)\2/g;
// Shared modules import each other by bare specifier too, so the rewrite
// walks the whole graph: each module becomes one Blob URL, memoised by its
// resolved URL so a shared module stays a singleton (registries, buses).
// Blob URLs cannot resolve a relative sourceMappingURL, so the comment is
// made absolute, and islandBlobSources maps each Blob URL back to the asset
// it came from so tooling (the editor overlay) can read stack frames.
var islandBlobModules = {};
var islandBlobSources = {};
function islandBlobModuleFor(url, map, depth) {
  if (islandBlobModules[url]) return islandBlobModules[url];
  if (depth > 12) return Promise.reject(new Error("island: import graph too deep at " + url));
  islandBlobModules[url] = fetch(url, { credentials: "same-origin" }).then(function(response) {
    if (!response.ok) throw new Error("island: " + response.status + " fetching " + url);
    return response.text();
  }).then(function(source) {
    var pending = [];
    var rewritten = source.replace(islandSpecifierPattern, function(whole, lead, quote, spec) {
      var resolved = islandResolveSpecifier(spec, map);
      if (!resolved) return whole;
      var placeholder = "\u0000island:" + pending.length + "\u0000";
      pending.push(islandBlobModuleFor(resolved, map, depth + 1));
      return lead + quote + placeholder + quote;
    }).replace(/(\/\/# sourceMappingURL=)(\S+)/, function(whole, lead, target) {
      try { return lead + new URL(target, url).href; } catch(e) { return whole; }
    });
    return Promise.all(pending).then(function(urls) {
      var body = rewritten.replace(/\u0000island:(\d+)\u0000/g, function(_, i) { return urls[Number(i)]; });
      var blobURL = URL.createObjectURL(new Blob([body], { type: "text/javascript" }));
      islandBlobSources[blobURL] = url;
      return blobURL;
    });
  });
  islandBlobModules[url].catch(function() { delete islandBlobModules[url]; });
  return islandBlobModules[url];
}
globalThis.__islandBlobSources = islandBlobSources;
function islandImportViaBlob(asset) {
  var map = islandReadImportMap();
  var url;
  try { url = new URL(asset, document.baseURI).href; } catch(e) { url = asset; }
  return islandBlobModuleFor(url, map, 0).then(function(blobURL) { return import(blobURL); });
}
function islandImportFailedOnSpecifier(err) {
  var message = err && err.message ? String(err.message) : "";
  return /resolve module specifier|Relative references must start|bare specifier/i.test(message);
}
function importIsland(asset) {
  return import(asset).catch(function(err) {
    if (!islandImportFailedOnSpecifier(err) || typeof fetch !== "function" || typeof Blob === "undefined") throw err;
    islandRuntimeEvent({ kind: "island", level: "warn", stage: "island.import.rewrite", title: "Import map not applied; rewriting specifiers", message: asset, asset: asset });
    return islandImportViaBlob(asset);
  });
}

// Hydration timing. The authoritative duration is a performance.now() delta
// captured around import(); it is stamped on the element as data-island-ms
// and carried by the island:hydrated event, so tooling never depends on the
// User Timing API. Marks/measures are a bonus for DevTools: every hydration
// gets a unique "<prefix>island:<element>#<n>" name so repeated elements on
// one page never collide. Everything here is best-effort and never throws.
var hydrationMarkPrefix = "";
var hydrationSequence = 0;
var ERROR_STAMP_MAX = 120;

function hydrationNow() {
  return typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
}

function hydrationStart(element) {
  if (typeof performance === "undefined" || !performance.mark) return "";
  var name = hydrationMarkPrefix + "island:" + element + "#" + (++hydrationSequence);
  try { performance.mark(name + ":start"); } catch(e) { return ""; }
  return name;
}

function hydrationMeasure(name) {
  if (!name || typeof performance === "undefined" || !performance.measure) return;
  try { performance.mark(name + ":end"); } catch(e) { return; }
  // The options form ({start, end}) is the modern API; older engines only
  // take positional mark names and throw a TypeError on an object.
  try { performance.measure(name, { start: name + ":start", end: name + ":end" }); return; } catch(e) {}
  try { performance.measure(name, name + ":start", name + ":end"); } catch(e) {}
}

// Stamps every tooling attribute the editor overlay reads; absent values
// (null/"") are skipped so pass-through hints never leave empty attributes.
// Hosts built on minimal DOM stubs may lack setAttribute; hydration never
// depends on it.
function stampIsland(el, attrs) {
  if (!el || typeof el.setAttribute !== "function") return;
  for (var key in attrs) {
    if (attrs[key] == null || attrs[key] === "") continue;
    try { el.setAttribute(key, attrs[key]); } catch(e) {}
  }
}

function dispatchIslandEvent(host, type, detail) {
  try {
    if (host && host.dispatchEvent && typeof CustomEvent === "function") {
      host.dispatchEvent(new CustomEvent(type, { bubbles: true, composed: true, detail: detail }));
    }
  } catch(e) {}
}

function hydrationEnd(mark, host, element, asset, ms) {
  hydrationMeasure(mark);
  try {
    if (host) {
      host.__islandError = null;
      if (typeof host.removeAttribute === "function") host.removeAttribute("data-island-error");
    }
  } catch(e) {}
  stampIsland(host, { "data-island-state": "hydrated", "data-island-ms": String(Math.round(ms * 100) / 100) });
  dispatchIslandEvent(host, "island:hydrated", { element: element, asset: asset, ms: ms, state: "hydrated" });
}

function hydrationFailed(host, element, asset, err, phase) {
  var message = String(err && err.message != null ? err.message : err == null ? "unknown error" : err);
  var stack = err && err.stack != null ? String(err.stack) : "";
  // Keep full diagnostics on the actual failed instance, including failures
  // that happen before the editor loads, without inflating HTML attributes.
  try { if (host) host.__islandError = { message: message, stack: stack, phase: phase, asset: asset || "" }; } catch(e) {}
  stampIsland(host, { "data-island-state": "failed", "data-island-error": message.slice(0, ERROR_STAMP_MAX) });
  dispatchIslandEvent(host, "island:failed", { element: element, asset: asset, error: message, stack: stack, phase: phase, state: "failed" });
}

class IslandElement extends HTMLElement {
  constructor() {
    super();
    this._load = this.getAttribute("load") || "idle";
    this._element = this.getAttribute("element");
    this._asset = this.getAttribute("asset");
    this._media = this.getAttribute("media");
  }

  // The theme customizer cannot reach the dev server (Chrome blocks
  // admin → localhost), so dev islands also carry data-asset-prod and the
  // runtime imports that one under Shopify.designMode. Production islands
  // never have the attribute and always resolve to `asset`.
  resolveAsset() {
    var shopify = globalThis.Shopify;
    var designMode = !!shopify && shopify.designMode === true;
    return (designMode && this.getAttribute("data-asset-prod")) || this._asset;
  }

  _import() {
    if (this._started) return;
    this._started = true;
    this._frame = requestAnimationFrame(() => {
      if (this.isConnected === false) { this._started = false; return; }
      var asset = this.resolveAsset();
      var context = { element: this._element || "", asset: asset || "" };
      var phase = "import";
      islandRuntimeEvent({
        kind: "island",
        level: "info",
        stage: "island.import.start",
        title: "Loading island",
        message: this._element || "",
        element: this._element || "",
        asset: asset || ""
      });
      try {
        if (!this._element) {
          throw new Error("island-element: missing element attribute");
        }
        if (!asset) {
          throw new Error("island-element: missing asset attribute");
        }

        var doesExist = globalThis.customElements.get(this._element);
        var $el = this.querySelector(this._element);

        if (!$el) {
          console.warn("island-element: could not find " + this._element + " (may load soon)");
          islandRuntimeEvent({
            kind: "island",
            level: "warn",
            stage: "island.element.missing",
            title: "Island element missing",
            message: this._element + " was not found inside island-element",
            element: this._element,
            asset: asset || ""
          });
          return; // Exit gracefully instead of throwing
        }

        phase = "props";
        var propsValue = islandPrepareProps($el);
        if (propsValue !== undefined) {
            islandRuntimeEvent({
              kind: "island",
              level: "info",
              stage: "island.props",
              title: "Island props hoisted",
              message: this._element || "",
              element: this._element || "",
              asset: asset || ""
            });
        }
        phase = "import";

        // Stamp the hydrated element so tooling (the editor overlay) can
        // still find islands after the host is swapped out, and forward the
        // visual-ownership hints the compiler passed through. Best effort:
        // never load-bearing for hydration itself.
        stampIsland($el, {
          "data-island": this._element || "",
          "data-island-asset": asset || "",
          "data-island-load": this._load || "",
          "data-island-state": "pending",
          "data-island-target": this.getAttribute("data-island-target"),
          "data-island-trigger": this.getAttribute("data-island-trigger")
        });

        var importPromise;
        var importTitle = "Island loaded";
        var mark = hydrationStart(this._element);
        var startedAt = hydrationNow();
        var mounted = false;
        if (doesExist === undefined) {
          importPromise = importIsland(asset);
        } else {
          importPromise = Promise.resolve();
          importTitle = "Island already defined";
        }

        importPromise.then(() => {
          if (this.isConnected === false) { this._started = false; return; }
          phase = "mount";
          if (!globalThis.customElements.get(this._element) || ($el.matches && !$el.matches(":defined"))) {
            throw new Error("Island module did not register " + this._element);
          }
          islandApplyProps($el, propsValue);
          this.replaceWith($el);
          mounted = true;
          return islandMounts.get($el);
        }).then(() => $el.updateComplete).then(() => {
          if (!mounted) return;
          hydrationEnd(mark, $el, this._element, asset, hydrationNow() - startedAt);
          islandRuntimeEvent({
            kind: "island",
            level: "info",
            stage: "island.import.succeeded",
            title: importTitle,
            message: this._element || "",
            element: this._element || "",
            asset: asset || ""
          });
        }).catch((err) => {
          hydrationFailed($el, this._element, asset, err, phase);
          islandRuntimeError(phase === "import" ? "Island import failed" : "Island mount failed", err, context);
        });
      } catch (err) {
        // Nothing was swapped yet, so the host itself carries the failure
        // for the editor overlay.
        hydrationFailed(this, this._element, asset, err, phase);
        islandRuntimeError("Island render failed", err, context);
      }
    });
  }

  _intersectionCallback(entries, observer) {
    for (var i = 0; i < entries.length; i++) {
      if (entries[i].isIntersecting) {
        this._import();
        observer.disconnect();
        return;
      }
    }
  }

  _mediaCallback(event) {
    event.preventDefault();
    if (this._media && globalThis.matchMedia(this._media).matches) {
      this._import();
      globalThis.removeEventListener("resize", this._boundMediaCallback);
    }
  }

  connectedCallback() {
    islandRuntimeEvent({
      kind: "island",
      level: "info",
      stage: "island.connect",
      title: "Island connected",
      message: this._element || "",
      element: this._element || "",
      asset: this.resolveAsset() || ""
    });
    switch (this._load) {
      case "eager":
        // Import as soon as the host connects (next animation frame). Use for
        // islands whose client re-renders above-the-fold SSR markup, so the
        // swap happens before first paint instead of showing up as CLS.
        this._import();
        break;
      case "load":
        this._import();
        break;
      case "idle":
        if (globalThis.requestIdleCallback) {
          this._idle = globalThis.requestIdleCallback(() => this._import());
        } else {
          this._timer = setTimeout(() => this._import(), 1);
        }
        break;
      case "visible": {
        var observer = new IntersectionObserver(
          (entries, obs) => this._intersectionCallback(entries, obs),
          { threshold: 1.0 }
        );
        this._observer = observer;
        observer.observe(this);
        break;
      }
      case "media":
        if (!this._media) {
          this._import();
          break;
        }
        if (globalThis.matchMedia(this._media).matches) {
          this._import();
          break;
        }
        this._boundMediaCallback = (e) => this._mediaCallback(e);
        globalThis.addEventListener("resize", this._boundMediaCallback);
        break;
      default:
        this._import();
        break;
    }
  }

  disconnectedCallback() {
    if (this._observer) this._observer.disconnect();
    if (this._boundMediaCallback) globalThis.removeEventListener("resize", this._boundMediaCallback);
    if (this._idle !== undefined && globalThis.cancelIdleCallback) globalThis.cancelIdleCallback(this._idle);
    if (this._frame !== undefined && globalThis.cancelAnimationFrame) globalThis.cancelAnimationFrame(this._frame);
    clearTimeout(this._timer);
    this._started = false;
  }
}

globalThis.customElements.get("island-element") ||
  globalThis.customElements.define("island-element", IslandElement);

// Clean up injected script tag
var injected = document.querySelector("[data-runtime]");
if (injected) injected.remove();

})()
